import { getUniqueKeys, numRoundTo5bb } from '@/helpers/helpers'
import { numFixedUnit } from '@gto/helpers/number-formatting'
import { getBlinds } from '@/store/solfigr/solfigr_helpers'
import { i18n, t, te } from '@/locales/i18n'
import { getGameformatGroup } from '@/store/gameformat/gameformat.ts'

function getGameformatQuery({ gameformats, lsKey, localOnly, customQuery }) {
  // url > localstorage > fallback

  let qq =
    customQuery ||
    (localOnly
      ? JSON.parse(localStorage.getItem(lsKey))
      : Object.fromEntries(new URL(document.location).searchParams)) ||
    {}

  const wantedUrlKeys = [
    'gametype', //
    'depth',
    'stacks',
    'depth_list',
    'stacks_list',
    'average_stack',
  ]
  qq = wantedUrlKeys.some(k => k in qq) ? qq : JSON.parse(localStorage.getItem(lsKey) || '{}')

  let gameformat = null

  let {
    gametype, //
    depth,
    stacks,
    depth_list,
    stacks_list,
    average_stack,
  } = qq

  gameformat = gameformats.find(
    i =>
      i.gametype === gametype &&
      `${i.depth || ''}` === `${depth || ''}` &&
      `${i.stacks || ''}` === `${stacks || ''}`,
  )

  if (customQuery || localOnly || !gameformat) {
    try {
      const localQuery = customQuery || JSON.parse(localStorage.getItem(lsKey)) || {}

      depth_list = localQuery.depth_list || ''
      stacks_list = localQuery.stacks_list || ''
      average_stack = localQuery.average_stack || ''

      gameformat = gameformats.find(
        i =>
          i.gametype === localQuery.gametype &&
          i.depth.toString() === localQuery.depth &&
          i.stacks.toString() === (localQuery.stacks || ''),
      )
    } catch (error) {
      // console.warn('_gameformat not valid json')
    }
  }

  if (!gameformat) {
    gameformat = gameformats.find(i => i.is_primary && !i.legacy)
  }
  if (!gameformat) {
    gameformat =
      gameformats.find(i => i.type === 'general' && !i.legacy) ||
      gameformats.find(i => !i.legacy) ||
      gameformats[0]
  }

  let availableFormats = gameformats.filter(i => i.gametype === gameformat.gametype)
  if (depth_list) {
    let availableDepths = availableFormats.map(i => i.depth)
    availableDepths = [...new Set(availableDepths)]

    depth_list = depth_list.split(',').filter(i => i)
    depth_list = depth_list.filter(strNum => {
      const r = availableDepths.includes(Number(strNum))
      return r
    })
  }

  // if (depth_list?.length && (stacks_list || stacks_type)) {
  if (depth_list?.length && stacks_list) {
    availableFormats = gameformats.filter(
      i => i.gametype === gameformat.gametype && depth_list.includes(i.depth.toString()),
    )
  }

  if (stacks_list) {
    const availableStacks = availableFormats.map(i => i.stacks)
    stacks_list = stacks_list.split(',').filter(i => i)
    stacks_list = stacks_list.filter(ss => availableStacks.includes(ss))
  }

  // if (stacks_type) {
  //   let availableStackTypes = availableFormats.map(i => i.stacks_type)
  //   availableStackTypes = [...new Set(availableStackTypes)]
  //   stacks_type = stacks_type.split(',').filter(i => i)
  //   stacks_type = stacks_type.filter(ss => availableStackTypes.includes(ss))
  // }

  if (average_stack) {
    let availableAvgStackTypes = availableFormats.map(i => i.avg_stack)
    availableAvgStackTypes = [...new Set(availableAvgStackTypes)]
    average_stack = average_stack
      .split(',')
      .filter(i => i)
      .map(i => Number(i))
    average_stack = average_stack.filter(ss => availableAvgStackTypes.includes(ss))
  }

  depth_list = depth_list?.length ? depth_list.join(',') : ''
  stacks_list = stacks_list?.length ? stacks_list.join(',') : ''
  // stacks_type = stacks_type?.length ? stacks_type.join(',') : ''
  average_stack = average_stack?.length ? average_stack.join(',') : ''

  const query = {
    gametype: gameformat.gametype,
    depth: gameformat.depth.toString(),
    stacks: gameformat.stacks.toString(),
    depth_list,
    stacks_list,
    // stacks_type,
    average_stack,
  }
  // console.log(query)
  return { query, gameformat }
}

function getGmfFilterQuery({ lsKey, localOnly, customQuery }) {
  let qq =
    customQuery ||
    (localOnly
      ? JSON.parse(localStorage.getItem(lsKey))
      : Object.fromEntries(new URL(document.location).searchParams)) ||
    {}

  // empty url default to localstorage
  qq = (Object.keys(qq).length ? qq : JSON.parse(localStorage.getItem(lsKey))) || {}

  const query = {
    gmff_stacks_type: qq.gmff_stacks_type || '',

    gmff_opening_type: qq.gmff_opening_type || '',
    gmff_tournament_phase: qq.gmff_tournament_phase || '',
    gmff_players: qq.gmff_players || '',
    gmff_variant: qq.gmff_variant || '',

    gmff_cash_drop: qq.gmff_cash_drop || '',

    gmff_biggest_stack: qq.gmff_biggest_stack || '',
    gmff_depth: qq.gmff_depth || '',
    gmff_type: qq.gmff_type || '',
    gmff_rake: qq.gmff_rake || '',
    gmff_opening_size: qq.gmff_opening_size || '',
    gmff__3bet_size: qq.gmff__3bet_size || '',
    gmff_simplified_position: qq.gmff_simplified_position || '',
    gmff_favorite: qq.gmff_favorite === 'true' || '',
  }

  return query
}

function getGmfTableQuery({ lsKey, localOnly, customQuery }) {
  let qq =
    customQuery ||
    (localOnly
      ? JSON.parse(localStorage.getItem(lsKey))
      : Object.fromEntries(new URL(document.location).searchParams)) ||
    {}

  // empty url default to localstorage
  qq = (Object.keys(qq).length ? qq : JSON.parse(localStorage.getItem(lsKey))) || {}

  const query = {
    gmfft_sort_key: qq.gmfft_sort_key || '0',
    gmfft_sort_order: qq.gmfft_sort_order || 'desc',
  }
  return query
}

function getGameformat({ item, gameformats, omitDepth = false }) {
  return (
    gameformats.find(
      gf =>
        (omitDepth ? true : gf.depth === (item.depth ? Number(item.depth) : '')) &&
        (omitDepth
          ? true
          : gf.stacks ===
            (item.stacks
              ? item.stacks
                  .split('-')
                  .map(i => Number(i))
                  .join('-')
              : '')) &&
        gf.gametype === (item.gametype || ''),
    ) || null
  )
}

function getStacksFromDepth({ depth, totalNum }) {
  return [...new Array(totalNum).keys()].map(pp => depth)
}

function getGameformatsFromGametypes({ api_gametypes, dev_showHidden }) {
  let res = api_gametypes.map(i => {
    const { opening_size } = i.info
    const { info } = i
    const format = i.format.toLowerCase()
    const is_hidden = info.hidden

    const exeptionStraddle =
      info.cash_type === 'straddle' ||
      format === 'straddle_ante_cash' ||
      format === 'straddle_ante_cash_6max'

    const group = exeptionStraddle ? 'cash' : format
    const premium_only = exeptionStraddle
    const gm_type = exeptionStraddle ? 'straddle_ante_cash' : format
    const { players } = i
    const type = i.type.toLowerCase()
    const base = {
      id: i.id,
      group,
      gm_type,
      players,
      gametype: i.name,
      gametype_format: i.format,
      type: type === 'simpletest' ? 'test' : type, // we want to show both as Research
      type_opening_size: `${type}${opening_size ? `_${opening_size}` : ''}`,

      variant: info.variant || '',
      preflop_only: info.postflop_players === 0,
      spots_preflop_postflop: info.spots || 'all_spots', // preflop_only

      is_soon: info.soon || false,
      maintenance: info.maintenance || false,

      legacy: i.info.legacy,
      format: info.tournament_name ? 'icm_events' : info.format,
      cash_type: info.cash_type,
      opening_size: info.opening_size || 'GTO',
      _3bet_size: info['3bet_size'] || 'GTO',
      simplified_position: info.simplified_position || null,
      rake: info.rake || 'no_rake',

      spins_type: info.spins_type || null,
      opening_type: info.opening_type || null,
      shortstack_type: info.shortstack_type || null,

      tournament_group: info.tournament_group || null,
      tournament_name: info.tournament_name || null,
      tournament_hand_number: info.tournament_hand_number || null,
      tournament_type: info.tournament_type || null,
      tournament_players: info.tournament_players || null,
      tournament_phase: info.tournament_phase || null,
      tournament_structure_id: info.tournament_data?.tournament_structure?.id || null,

      stacks_type: info.stacks_type || null,
      avg_stack: info.avg_stack || null,
      cash_drop: info.cash_drop || 'no_cash_drop',

      // only relevant to icm events where some hands are duplicated and skipped
      duplicates: info.duplicates || null,

      ...(info.husng_type ? { husng_type: info.husng_type } : {}),
      ...(info.spins_type || info.husng_type
        ? {
            husng_or_spins_type: info.spins_type || info.husng_type,
          }
        : {}),

      info: {
        spots: info.spots,
        rake_text: info.rake_text || info.rake,
        sizings: info.sizings,
        accuracy: info.accuracy,
        ante: info.ante || 0,
        note: info.note,
        flop_reports: info.flop_reports,
        turn_reports: info.turn_reports,
        premium_only,
      },
      depth: '',
      stacks: '',
      stack_rounding: '',
      is_subscribed: false,
      subscription_tier: null,
      is_favorite: false,
      is_hidden,
      is_primary: false,

      // icm events
      player_names: info.players || null,
      stream_url: info.stream_url || null,
    }
    const { game_modes } = i
    const r = game_modes.map(gm => {
      const depth = Number(gm.depth)
      // const stack_rounding = Number(gm.info.stack_rounding)
      const stack_rounding = Number(gm.info.stack_rounding || numRoundTo5bb(depth))

      const { is_subscribed } = gm
      const { subscription_tier } = gm
      const { is_favorite } = gm

      const { postflop_players } = gm.info
      const { flop_reports } = gm.info
      const { turn_reports } = gm.info
      const depthHidden = gm.info.hidden
      const is_primary = gm.info.primary
      const variant = gm.info.variant || ''
      const maintenance = gm.info.maintenance || false
      const bounties = gm.info.bounties || null

      const tournament_group = gm.info.tournament_group || null
      const tournament_name = gm.info.tournament_name || null
      const tournament_hand_number = gm.info.tournament_hand_number || null
      const tournament_type = gm.info.tournament_type || null
      const tournament_players = gm.info.tournament_players || null
      const tournament_phase = gm.info.tournament_phase || null
      const tournament_structure_id = gm.info.tournament_structure_id || null
      const stacks_type = gm.info.stacks_type || null
      const avg_stack = gm.info.avg_stack || null

      const player_names = gm.info.players || null
      const stream_url = gm.info.stream_url || null

      const opening_type = gm.info.opening_type || null
      const simplified_position = gm.info.simplified_position || null
      const spins_type = gm.info.spins_type || null
      const husng_type = gm.info.husng_type || null

      const biggest_stack = gm.stacks?.length ? Math.max.apply(0, gm.stacks) : null
      const stacks = (gm.stacks || []).map(ss => Number(ss)).join('-') || ''
      const _3bet_size = gm.info['3bet_size']
      const preflop_only = postflop_players === 0 || base.preflop_only

      const duplicates = gm.info.duplicates || null
      const depthBase = {
        ...base,

        id: gm.id,

        // depth and stack rounding are basically the same exept for cashDrop
        depth,
        stack_rounding,

        is_subscribed,
        is_favorite,
        is_primary,
        subscription_tier,
        ...(depthHidden ? { is_hidden: true } : {}),
        ...(variant ? { variant } : {}),
        ...(maintenance ? { maintenance } : {}),

        preflop_only,
        solution_completeness: preflop_only ? 'preflop_only' : 'all_spots',

        flop_reports: flop_reports || base.flop_reports,
        turn_reports: turn_reports || base.turn_reports,
        spots: postflop_players ? `${postflop_players}handed` : '',

        ...(opening_size ? { opening_size } : {}),
        ...(_3bet_size ? { _3bet_size } : {}),
        ...(simplified_position ? { simplified_position } : {}),

        ...(opening_type ? { opening_type } : {}),

        ...(spins_type ? { spins_type } : {}),
        ...(husng_type ? { husng_type } : {}),
        ...(spins_type || husng_type
          ? {
              husng_or_spins_type: spins_type || husng_type,
            }
          : {}),

        ...(tournament_group ? { tournament_group } : {}),
        ...(tournament_name ? { tournament_name } : {}),
        ...(tournament_hand_number ? { tournament_hand_number } : {}),
        ...(tournament_type ? { tournament_type } : {}),
        ...(tournament_players ? { tournament_players } : {}),
        ...(tournament_phase ? { tournament_phase } : {}),
        ...(tournament_structure_id ? { tournament_structure_id } : {}),

        ...(stacks_type ? { stacks_type } : {}),
        ...(avg_stack ? { avg_stack } : {}),
        ...(biggest_stack ? { biggest_stack } : {}),
        ...(bounties ? { bounties } : {}),

        ...(player_names ? { player_names } : {}),
        ...(stream_url ? { stream_url } : {}),

        ...(duplicates ? { duplicates } : {}),
        stacks,
      }
      return depthBase
    })
    return r
  })
  res = res.flat(1)

  res = res.map(gmf => {
    // Set default for Cash
    if (gmf.gametype === 'Cash6m500zGeneral') {
      return { ...gmf, is_primary: true }
    }

    return gmf
  })

  if (!dev_showHidden) {
    res = res.filter(i => !i.is_hidden)
  }

  return res
}

function getStacksDisplayname(stacks) {
  const res = stacks
    .split('-')
    .map(n => numRoundTo5bb(Number(n)))
    .join('-')
  return res
}

function getGmfNameConf({ gameformat }) {
  const gmf = gameformat
  const { group } = gmf
  let res = []

  if (group === 'husng') {
    // Hu SnG Classic Complex 25
    res = {
      group: t(`gametypes.groups.${group}`),
      hunsg_type: t(`gametypes.${gmf.husng_type}`),
      type: t(`gametypes.${gmf.type}`),
      depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.depth) }),
    }
  } else if (group === 'spins') {
    // Spin & Go Classic General 25-25-25bb
    res = {
      group: t(`gametypes.groups.${group}`),
      spins_type: t(`gametypes.${gmf.spins_type}`),
      type: t(`gametypes.${gmf.type}`),
      opening_type: t(`gametypes.${gmf.opening_type.replace('.', '_')}`),
      stacks: t('common.stack_bb', { stack: getStacksDisplayname(gmf.stacks) }),
    }
  } else if (group === 'mtt') {
    // MTT Heads-up 60bb
    // MTT ICM Tournament-pahse Avg.35bb
    // MTT ICM PKO Tournament-pahse Avg.35bb
    // MTT Chipev With limps Avg.35bb
    // MTT Chipev With limps Avg.35bb
    const displayAvgStack = gmf.format === 'icm' || gmf.format === 'chipev'
    const formatText =
      gmf.tournament_type === 'pko' ? t(`gametypes.icm_pko`) : t(`gametypes.${gmf.format}`)
    res = {
      group: t(`gametypes.groups.${group}`),
      format: formatText,
      ...(gmf.format === 'icm'
        ? { tournament_phase: t(`gametypes.${gmf.tournament_phase}_players`) }
        : {}),
      ...(gmf.tournament_name
        ? { tournament_name: t(`gametypes.tournament_name_short.${gmf.tournament_name}`) }
        : {}),
      ...(gmf.variant ? { variant: t(`gametypes.${gmf.variant}`) } : {}),
      ...(displayAvgStack
        ? { avg_stacks: t('common.avg_stack_bb', { stack: gmf.avg_stack }) }
        : {}),
      ...(!displayAvgStack
        ? { depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.depth) }) }
        : {}),
    }
  } else if (group === 'cash') {
    if (gmf.format === 'hu') {
      // Cash Heads-up NL500 Advanced 100bb
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.hu`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        type: t(`gametypes.${gmf.type}`),
        depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.depth) }),
      }
    } else if (gmf.format?.includes('straddle')) {
      // Cash Straddle+Ante 0.1bb CAP General 100bb
      const halfDepth = numRoundTo5bb(gmf.depth / 2)
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        type: t(`gametypes.${gmf.type}`),
        depth: t('common.stack_bb_str', {
          stack: numRoundTo5bb(gmf.depth),
          str: halfDepth,
        }),
      }
    } else if (gmf.cash_drop !== 'no_cash_drop') {
      // Cash NL50 Cash Drop 10bb No Limps
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash_drop: t(`gameformat.cash_drop_xxx`, {
          cash_drop: t(`gametypes.${gmf.cash_drop}`),
        }),
        variant: t(`gametypes.${gmf.variant}`),
        depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.stack_rounding) }), // stack_rounding exeption
      }
    } else if (gmf.cash_type === 'regular' && gmf.format === '8max') {
      // Cash NL50 General 2.5x 200
      // Cash NL50 General 200
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`),
        type_opening_size: `${t(`gametypes.${gmf.type}`)} ${t(
          `gametypes.${gmf.opening_size}`,
        )} ${gmf.simplified_position ? t(`gametypes.${gmf.simplified_position}`) : ''}`,
        depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.stack_rounding) }), // stack_rounding exeption
      }
    } else if (gmf.shortstack_type) {
      // All-in or Fold 3max cEV
      res = {
        shortstack_type: t(`gametypes.${gmf.shortstack_type}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`),
      }
    } else if (gmf.variant && gmf.format === '4max') {
      // All-in or Fold 3max cEV
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`), // trainer only
        type_opening_size: `${t(`gametypes.${gmf.type}`)} ${t(
          `gametypes.${gmf.opening_size}`,
        )} ${gmf.simplified_position ? t(`gametypes.${gmf.simplified_position}`) : ''}`,
        depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.stack_rounding) }), // stack_rounding exeption
        variant: t(`gametypes.${gmf.variant}`),
      }
    } else if (gmf.variant && gmf.format === '7max') {
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`), // trainer only
        type_opening_size: `${t(`gametypes.${gmf.type}`)} ${t(
          `gametypes.${gmf.opening_size}`,
        )} ${gmf.simplified_position ? t(`gametypes.${gmf.simplified_position}`) : ''}`,
      }
    } else if (
      (gmf.cash_type === 'straddle' ||
        gmf.cash_type === 'ante_straddle' ||
        gmf.cash_type === 'double_straddle' ||
        gmf.cash_type === 'mississippi_straddle') &&
      gmf.format === '8max'
    ) {
      res = {
        format: t(`gametypes.${gmf.cash_type}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`), // trainer only
        type_opening_size: `${t(`gametypes.${gmf.type}`)} ${t(
          `gametypes.${gmf.opening_size}`,
        )} ${gmf.simplified_position ? t(`gametypes.${gmf.simplified_position}`) : ''}`,
      }
    } else {
      // Cash NL50 General 2.5x 200 3b: Small
      // Cash NL50 General 200 3b: GTO
      res = {
        group: t(`gametypes.groups.${group}`),
        format: t(`gametypes.${gmf.format}`),
        rake: te(`gametypes.${gmf.rake}`, gmf.rake),
        cash69max_type: t(`gametypes.${gmf.type}`), // trainer only
        type_opening_size: `${t(`gametypes.${gmf.type}`)} ${t(
          `gametypes.${gmf.opening_size}`,
        )} ${gmf.simplified_position ? t(`gametypes.${gmf.simplified_position}`) : ''}`,
        depth: t('common.stack_bb', { stack: numRoundTo5bb(gmf.stack_rounding) }), // stack_rounding exeption
        _3bet_size: t(`common._3bet_xxx`, {
          str: t(`gametypes.${gmf._3bet_size}`),
        }),
      }
    }
  }
  return res
}

function getCustomTreeNameConf({ customTree }) {
  const cTree = customTree
  const players = cTree?.players || []
  const playerOOP = players.find(p => p.position === 'OOP')
  const playerIP = players.find(p => p.position === 'IP')
  const rakeCap = cTree?.rake?.cap_in_chips || 0
  const rakePercent = cTree?.rake?.pct_of_pot || 0

  const getPlayerHtmlPosition = pos => {
    if (pos === 'UTG+1') {
      return `UTG1`
    }
    if (pos === 'UTG+2') {
      return `UTG2`
    }
    return pos
  }

  const allow_limp = cTree?.bet_sizes?.street_bet_sizes?.[0]?.position_bet_sizes?.[0]?.allow_limp
  const l_players = {
    players: t(`solfigr.oop_vs_ip`, {
      oop: getPlayerHtmlPosition(playerOOP?.display_position) || 'OOP',
      ip: getPlayerHtmlPosition(playerIP?.display_position) || 'IP',
    }),
  }
  const rake = rakeCap || rakePercent ? t(`solfigr.gmf.rake`, { rakeCap, rakePercent }) : ''
  // const rake = rakeCap || rakePercent ? `${rakePercent}% ${rakeCap}bb` : ''
  const res =
    cTree?.starting_street === 'FLOP'
      ? {
          ...l_players,
          depth: t(`solfigr.gmf.stack`, {
            num: numFixedUnit(Number(cTree?.players[0]?.stack || 0), 'bb'),
          }),
          pot: t(`solfigr.gmf.pot`, { num: numFixedUnit(Number(cTree?.pot), 'bb') }),
          rake,
        }
      : {
          ...l_players,
          stack_and_blinds: t('solfigr.gmf.stack_and_blinds', {
            stack: numFixedUnit(
              Math.min(
                Number(cTree?.players[0]?.stack), //
                Number(cTree?.players[1]?.stack),
              ),
              'bb',
            ),
            sb: numFixedUnit(Number(getBlinds(cTree?.players)?.SB), 'bb'),
            bb: numFixedUnit(Number(getBlinds(cTree?.players)?.BB), 'bb'),
          }),
          ...(Number(cTree?.ante) && {
            ante: t('solfigr.gmf.ante', {
              ante: numFixedUnit(Number(cTree?.ante), 'ante'),
              ante_distribution_method: cTree?.ante_distribution_method
                ? t(`solfigr.ante_distribution_method.${cTree?.ante_distribution_method}`)
                : '',
            }),
          }),
          limps: allow_limp ? t('solfigr.has_limps') : t('solfigr.no_limps'),
          rake,
        }
  return res
}

function getGameformatOptionHtml({
  gameformat,
  // gametype_list,
  omitKeys = ['cash69max_type'],
  includeKeys = null,
}) {
  const nameConf = getGmfNameConf({ gameformat })
  let res = {}
  if (omitKeys) {
    omitKeys.forEach(k => {
      delete nameConf[k]
    })
  }
  if (includeKeys) {
    includeKeys.forEach(k => {
      if (nameConf[k]) {
        res[k] = nameConf[k]
      }
    })
  } else {
    res = nameConf
  }
  res = Object.values(res)
  res = res.join(' • ')
  return res
}

function getFilteredGameformats({ gameformats, keyPairs }) {
  const res = gameformats.filter(gg => {
    const r = gg[keyPairs[0]] === keyPairs[1]
    return r
    // return gg[keyPairs[0]] === keyPairs[1]
  })
  return res
}

// ----------
function getGameformatsByKey({ gameformats, key }) {
  let res = getUniqueKeys({ arr: gameformats, key })
  // console.log(res)
  // console.log(gameformats, groups)
  res = res.map(g => {
    const formats = getFilteredGameformats({ gameformats, keyPairs: [key, g] })
    const r = {
      id: g,
      gameformats: formats,
    }
    return r
  })
  // console.log(res)
  return res
}
function getGmfBlock({ formats, id }) {
  const formatsByKey = getGameformatsByKey({ gameformats: formats, key: id })
  return formatsByKey
}
function getGmfGroupBlocks({ formats, blocks }) {
  let res = { id: 'root', formats }
  blocks.forEach((ccgg, idx) => {
    res = blocks[idx - 1] || res
    const prevFormats = res?.formats
    const formatsByKey = getGmfBlock({ formats: prevFormats, id: ccgg.id })
    ccgg.formatsByKey = formatsByKey
    ccgg.formats = formatsByKey.reduce((a, b) => [...a, ...b.formats], [])
    const r = {}
    formatsByKey.forEach(g => {
      r[g.id] = g
    })
    res[ccgg.id] = r
  })
  // console.log(res)
  return res
}

function getHasLock({ sub, subsConf, self }) {
  if (self.id === 'root') {
    return false
  }
  // if (subsConf.id === 'group') {
  //   return false
  // }

  const gameformats = sub.gameformats || []
  const res = gameformats.every(g => !g.subscription_tier)
  return res
}

function getNestedGameformats({ self, index = 0, conf }) {
  const { gameformats } = self
  const subsConf = conf[index]
  const subGroupId = subsConf?.id

  let subs = subGroupId ? getGameformatsByKey({ gameformats, key: subGroupId }) : null
  subs = subs
    ? subs.map(sub => {
        const conditions = {
          ...self.conditions,
          [subGroupId]: sub.id,
        }

        const hasLock =
          self.hasLock || getHasLock({ gameformats: sub.gameformats, sub, subsConf, self })

        const next = {
          self: {
            ...sub,
            id: sub.id,
            groupId: subGroupId,
            gameformats: sub.gameformats,
            conditions,
            // legacy,
            // someLegacy,
            hasLock,
          },
          conf: subsConf.blocks?.[sub.id] || conf,
          index: subsConf.blocks?.[sub.id] ? 0 : index + 1,
        }
        const r = getNestedGameformats({
          self: next.self,
          index: next.index,
          conf: next.conf,
        })
        return r
      })
    : null

  const res = {
    ...self,
    subsConf,
    subs,
  }
  return res
}

// GMFoptimized
function getActiveGmfSubs({ acc, item, activeFormat, level = 0, block_ids }) {
  if (item.subs) {
    if (block_ids) {
      if (block_ids.includes(item.subsConf.block_id)) {
        acc[item.subsConf.block_id] = item
      }
    } else {
      acc[item.subsConf.block_id] = item
    }
    for (let index = 0; index < item.subs.length; index++) {
      const sub = item.subs[index]
      if (sub.subs) {
        const active = activeFormat[sub.groupId] === sub.id
        if (active) {
          acc = getActiveGmfSubs({
            acc,
            item: sub,
            activeFormat,
            level: level + 1,
            block_ids,
          })
          break
        }
      }
    }
  }

  return acc
}

function getGmfConfig() {
  const depth = {
    id: 'depth',
    block_id: 'depth',
    name: 'gameformat.stack',
    order: 'desc',
    getOptionName: ({ option }) => numRoundTo5bb(Number(option.id)),
  }
  const type = {
    id: 'type',
    block_id: 'type',
    getTooltip: conf => {
      const tts = {
        rest: 'tooltips.gmf_type_rest',
      }
      return tts[conf.conditions.group] || tts.rest
    },
    getTooltipHtml: options => {
      if (options && options.length) {
        return options
          .map(
            opt =>
              `<b>${t(`tooltips.gmf_types.${opt}`)}</b>: ${t(`tooltips.gmf_types.${opt}_text`)}`,
          )
          .join('<br>')
      }
      return t('tooltips.gmf_type_rest')
    },
    getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
    order: [
      'simple',
      'simple_2_5x',
      'simplified',
      'general',
      'general_2_5x',
      'test',
      'advanced',
      'complex',
      'basic',
    ],
  }
  const format = {
    id: 'format',
    block_id: 'format',
    name: 'gameformat.players',
    tooltip: 'tooltips.gmf_players',
    getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
    order: ['hu', '6max', '8max', '8Max', '9max', '6max_hu', '6max_straddle', '8max_straddle'],
  }
  const res = {
    group: {
      id: 'group',
      block_id: 'group',
      name: 'common.solutions',
      tooltip: 'tooltips.gmf_solutions',
      getOptionName: ({ option, row }) => t(`gametypes.groups.${option.id}`),
      order: ['cash', 'mtt', 'spins', 'husng'],
    },
    cash_type: {
      id: 'cash_type',
      block_id: 'cash_type',
      tooltip: 'tooltips.gmf_cash_type',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: [
        'regular',
        'shortstack',
        'ante',
        'straddle',
        'ante_straddle',
        'double_straddle',
        'mississippi_straddle',
      ],
    },
    shortstack_type: {
      id: 'shortstack_type',
      block_id: 'shortstack_type',
      tooltip: 'tooltips.gmf_shortstack_type',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['gg_ai_or_fold', 'winamax_short', 'acr_short', 'advanced'],
    },

    format,
    format_straddle: {
      ...format,
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}_max`),
      block_id: 'format_straddle',
    },
    format_mtt: {
      id: 'format',
      block_id: 'format_mtt',
      tooltip: 'tooltips.gmf_format_mtt',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['hu', 'chipev', 'icm', 'icm_events'],
      allToBeOptions: ['hu', 'chipev', 'icm', 'icm_events'],
    },
    depth,
    depth_cash: {
      ...depth,
      block_id: 'depth_cash',
    },
    depth_husng: {
      ...depth,
      block_id: 'depth_husng',
    },
    depth_mtt_hu: {
      ...depth,
      block_id: 'depth_mtt_hu',
    },
    depth_mtt_chipev_8: {
      ...depth,
      block_id: 'depth_mtt_chipev_8',
    },
    depth_mtt_chiv_ev_preflop_only: {
      ...depth,
      block_id: 'depth_mtt_chiv_ev_preflop_only',
    },
    cash_drop: {
      id: 'cash_drop',
      block_id: 'cash_drop',
      tooltip: 'tooltips.gmf_cash_drop',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['no_cash_drop', '10bb', '20bb'],
    },
    solution_completeness: {
      id: 'solution_completeness',
      block_id: 'solution_completeness',
      tooltip: 'tooltips.gmf_solution_completeness',
      order: ['all_spots', 'preflop_only'],
      getOptionName: ({ option, row }) => t(`gameformat.${option.id}`),
    },
    spots_preflop_postflop: {
      id: 'spots_preflop_postflop',
      block_id: 'spots_preflop_postflop',
      title: 'gameformat.solution_completeness',
      tooltip: 'tooltips.gmf_solution_completeness',
      getOptionName: ({ option, row }) => t(`gameformat.${option.id}`),
      order: ['all_spots', 'preflop_only'],
    },
    variant: {
      id: 'variant',
      block_id: 'variant',
      getTooltip: conf => {
        const dict = {
          chipev: 'tooltips.gmf_variant_mtt_chipev',
          '4max': 'tooltips.gmf_variant_cash_ante_4max',
        }
        return dict[conf.conditions.format]
      },
      order: ['with_limps', 'no_limps'],
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
    },
    rake: {
      id: 'rake',
      block_id: 'rake',
      tooltipFallbackKey: 'tooltips.gmf_rake',
      useTooltipFallbackKey: false,
      getTooltip: conf => {
        const dict = {
          hu: 'tooltips.gmf_rake_hu',
          '4max': 'tooltips.gmf_rake_4max',
          '6max': `tooltips.gmf_rake_6max_${conf.conditions.solution_completeness}`,
          '7max': 'tooltips.gmf_rake_7max',
          '8max': 'tooltips.gmf_rake_8max',
          '8max_straddle': 'tooltips.gmf_rake_8max_straddle',
          '8max_ante_straddle': 'tooltips.gmf_rake_8max_ante_straddle',
          '9max': 'tooltips.gmf_rake_9max',
        }
        const cash_type_dict = {
          straddle: '_straddle',
          ante_straddle: '_ante_straddle',
        }
        const key = `${conf.conditions.format}${cash_type_dict[conf.conditions.cash_type] || ''}`
        return dict[key]
      },
      getOptionName: ({ option, row }) =>
        te(`gametypes.${option.id}`) ? t(`gametypes.${option.id}`) : option.id,
      order: [
        'cEV',
        'NL10',
        'NL25',
        'NL50',
        'NL100',
        'NL200',
        'NL400',
        'NL500',
        'NL1000',
        'NL2000',
        'NL5000',
        'NL10000',
        'GGrc',
        'GG NL50',
        'GG NL500',
        'LIVE10',
      ],
    },
    type,
    type_cash: {
      ...type,
      block_id: 'type_cash',
    },
    stacks_cash: {
      id: 'stacks',
      block_id: 'stacks_cash',
    },
    stacks_mtt: {
      id: 'stacks',
      block_id: 'stacks_mtt',
    },
    stacks_spins: {
      id: 'stacks',
      block_id: 'stacks_spins',
    },
    tournament_type: {
      id: 'tournament_type',
      block_id: 'tournament_type',
      tooltip: 'tooltips.gmf_tournament_type_tra01',
      tooltipProps: {
        linkText: !i18n.g1
          ? `<a class='link-underline' href='https://blog.gtowizard.com/the-theory-of-progressive-knockout-tournaments/' target='_blank'>${t(
              `guides.learn_more_about_pko`,
            )}</a>`
          : ``,
      },
      // guide: 'icm_pko',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['regular', 'pko'],
      allToBeOptions: ['regular', 'pko'],
    },
    tournament_group: {
      id: 'tournament_group',
      block_id: 'tournament_group',
      tooltip: 'tooltips.gmf_tournament_group',
      getOptionName: ({ option }) => option.id,
    },
    tournament_name: {
      id: 'tournament_name',
      block_id: 'tournament_name',
      tooltip: 'tooltips.gmf_tournament_name',
      getOptionName: ({ option }) => t(`gametypes.tournament_name.${option.id}`),
    },
    tournament_players: {
      id: 'tournament_players',
      block_id: 'tournament_players',
      tooltip: 'tooltips.gmf_tournament_players',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}_players`),
    },
    tournament_phase: {
      id: 'tournament_phase',
      block_id: 'tournament_phase',
      tooltip: 'tooltips.gmf_tournament_phase',
      getOptionName: ({ option, row, rows }) => t(`gametypes.${option.id}_players`),
      order: [
        'START',
        'PCT90',
        'PCT75',
        'PCT70',
        'PCT50',
        'PCT37',
        'PCT25',
        'BUBBLEEARLY',
        'BUBBLEMID',
        'BUBBLELATE',
        'PCT10',
        'PCT5',
        'T3',
        'T2',
        'FT',
      ],
    },
    stacks_type: {
      id: 'stacks_type',
      block_id: 'stacks_type',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: [
        'SYMMETRIC',
        'ASYMMETRIC',
        'ASYMMETRIC_NEAR',
        'ASYMMETRIC_FAR',
        'ASYMMETRIC_SHORT',
        'ASYMMETRIC_BIGSTACK',
      ],
    },
    biggest_stack: {
      id: 'biggest_stack',
      block_id: 'biggest_stack',
      name: 'gameformat.biggest_stack',
      order: 'desc',
      getOptionName: ({ option, row }) => numRoundTo5bb(Number(option.id)),
    },
    husng_or_spins_type: {
      id: 'husng_or_spins_type',
      block_id: 'husng_or_spins_type',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['regular', 'with_ante'],
    },
    opening_type: {
      id: 'opening_type',
      block_id: 'opening_type',
      tooltip: 'tooltips.gmf_opening_type',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id.replace('.', '_')}`),
      order: [
        'default', //  GTO
        'R2', //  2x
        'R2ONLY', //  2x Only
        'R2.25', //  2.25x
        'R2.5', //  2.5x
        'R2.5ONLY', //  2.5x No limp
        'R2.75', //  2.75x
        'R3', //  3x
        '225x', // "2.25x"
      ],
    },
    opening_size: {
      id: 'opening_size',
      block_id: 'opening_size',
      getOptionName: ({ option, row }) =>
        t(`gametypes.${option.id === 'gto' ? 'gto_mixed' : option.id}`),
      order: [
        'gto',
        '2x',
        '225x',
        '2.25x',
        '25x',
        '2.5x',
        '3x',
        '4x',
        '5x',
        '6x',
        '7x',
        '8x',
        '10x',
        '12x',
        '14x',
        '4bb',
        '5bb',
        '6bb',
        '7bb',
        '8bb',
        '10bb',
        '12bb',
        '14bb',
      ],
    },
    _3bet_size: {
      id: '_3bet_size',
      block_id: '_3bet_size',
      tooltip: 'tooltips.gmf_cash__3bet_size',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['gto', 'smaller'],
    },
    simplified_position: {
      id: 'simplified_position',
      block_id: 'simplified_position',
      getOptionName: ({ option, row }) => t(`common.${option.id}`),
      order: ['oop', 'ip'],
    },
    avg_stack: {
      id: 'avg_stack',
      block_id: 'avg_stack',
    },
    type_opening_size: {
      id: 'type_opening_size',
      block_id: 'type_opening_size',
      tooltip: 'tooltips.gmf_type_opening_size',
      getOptionName: ({ option, row }) => t(`gametypes.${option.id}`),
      order: ['general_gto', 'general_25x', 'simple_25x', 'simple_gto', 'complex_gto', 'basic_gto'],
    },
    players: {
      id: 'players',
      block_id: 'players',
      tooltip: 'tooltips.gmf_players',
      order: 'asc',
    },
  }
  return res
}

function getAllNestedGameformats({
  key, //
  gameformats,
  previousNested,
  allGmfs,
}) {
  const bb = getGmfConfig()
  const root = {
    id: 'root',
    key,
    gameformats,
    legacy: false,
  }
  let res = getNestedGameformats({
    self: root,
    index: 0,
    conf: [bb.group],
  })

  const groupBlocksStudy = {
    cash: [
      {
        ...bb.cash_type,
        blocks: {
          ante_straddle: [
            bb.solution_completeness,
            bb.format_straddle, //
            bb.depth_cash,
          ],
          shortstack: [
            bb.shortstack_type, //
            bb.format,
            { ...bb.rake, useTooltipFallbackKey: true },
            bb.avg_stack,
          ],
          ante: [
            bb.format, //
            bb.depth_cash,
          ],
        },
      },
      {
        ...bb.format,
        blocks: {
          hu: [
            {
              ...bb.solution_completeness,
              blocks: {
                preflop_only: [
                  bb.depth_cash, //
                ],
                all_spots: [
                  bb.depth_cash, //
                  bb.type,
                ],
              },
            },
          ],
          '6max': [
            bb.solution_completeness, //
            bb.type_cash,
          ],
        },
      },
      bb.type_cash,
    ],
    spins: [
      bb.husng_or_spins_type, //
      bb.stacks_spins,
      bb.opening_type,
      bb.type,
    ],
    mtt: [
      {
        ...bb.format_mtt,
        blocks: {
          icm: [
            bb.tournament_type, //
            bb.tournament_players,
            bb.tournament_phase,
            bb.players,
            bb.stacks_mtt,
          ],
          chipev: [
            {
              ...bb.players,
              blocks: {
                8: [
                  {
                    ...bb.spots_preflop_postflop,
                    blocks: {
                      preflop_only: [
                        bb.stacks_mtt, //
                      ],
                      all_spots: [
                        bb.depth_mtt_chipev_8, //
                        bb.variant,
                      ],
                    },
                  },
                ],
              },
            },
            bb.stacks_mtt,
          ],
          hu: [
            bb.depth_mtt_hu, //
          ],
          icm_events: [
            bb.tournament_group,
            bb.tournament_name, //
            bb.tournament_phase,
            bb.stacks_mtt,
          ],
        },
      },
    ],
    husng: [
      bb.husng_or_spins_type, //
      bb.depth_husng,
      bb.type,
    ],
  }

  const groupBlocksTrainer = {
    cash: [
      {
        ...bb.cash_type,
        blocks: {
          ante_straddle: [
            bb.solution_completeness, //
            bb.format_straddle,
            bb.type,
            { ...bb.rake, useTooltipFallbackKey: true },
            bb.depth_cash,
          ],
          shortstack: [
            bb.shortstack_type, //
            bb.format,
            bb.rake,
            bb.avg_stack,
          ],
        },
      },
      {
        ...bb.format,
        blocks: {
          hu: [
            {
              ...bb.solution_completeness,
              blocks: {
                preflop_only: [
                  bb.type, //
                  bb.rake,
                  bb.depth_cash,
                ],
                all_spots: [
                  bb.type, //
                  bb.depth_cash,
                ],
              },
            },
          ],
          '6max': [
            bb.solution_completeness, //
            bb.type,
            bb.rake,
            bb.depth_cash,
          ],
        },
      },
      bb.solution_completeness, //
      bb.type,
      bb.rake,
      bb.depth_cash,
    ],
    spins: [
      bb.husng_or_spins_type, //
      bb.type,
      bb.stacks_spins,
    ],
    mtt: [
      {
        ...bb.format_mtt,
        blocks: {
          icm: [
            bb.tournament_type, //
            bb.tournament_players,

            bb.avg_stack,
          ],
          chipev: [
            {
              // 8 players
              ...bb.players,
              blocks: {
                8: [
                  {
                    ...bb.spots_preflop_postflop,
                    blocks: {
                      preflop_only: [
                        bb.stacks_mtt, //
                      ],
                      all_spots: [
                        bb.depth_mtt_chipev_8, //
                      ],
                    },
                  },
                ],
              },
            },
            bb.avg_stack,
          ],
          hu: [
            bb.depth_mtt_hu, //
          ],
          icm_events: [
            bb.tournament_group, //
            bb.tournament_name,
            bb.tournament_phase,
            bb.avg_stack,
          ],
        },
      },
    ],
    husng: [
      bb.husng_or_spins_type, //
      bb.type,
      bb.depth_husng,
    ],
  }

  const groupBock = key === 'study' ? groupBlocksStudy : groupBlocksTrainer
  const previousSubs = previousNested?.subs || []
  res = {
    ...res,
    gameformats: allGmfs,
    subs: [
      ...res.subs.map(i => {
        const block = groupBock[i.id]
        const r = getNestedGameformats({
          self: i,
          index: 0,
          conf: block,
        })
        return r
      }),
      ...previousSubs,
    ],
  }

  // console.log('getAllNestedGameformats', key, res)
  return res
}

function getNearestGmfs({ gameformats, compareKeys, gameformat, index = 0 }) {
  const key = compareKeys[index]
  let res = gameformats.filter(gg => gg[key] === gameformat[key])
  res = res.length ? res : gameformats
  if (compareKeys[index + 1]) {
    return getNearestGmfs({ gameformats: res, compareKeys, gameformat, index: index + 1 })
  }
  return res
}

function getPreferredGmf({ gameformats, gameformat, compareKeys }) {
  let gmfWithMaxHits = null

  if (compareKeys?.length) {
    // compareKeys = [
    //   'group',
    //   'format',
    //   'depth',
    //   'rake',
    //   'type',
    //   'stacks',
    //   'variant',
    //   'format',
    //   'tournament_type',
    //   'tournament_players',
    //   'tournament_phase',
    //   'players',
    //   'stacks_type',
    // ]
    // let nearestGmfs = gameformats
    let nearestGmfs = getNearestGmfs({ gameformats, compareKeys, gameformat })
    let nearestGmfsByKey = compareKeys.map(key => {
      const rowGameformats = nearestGmfs.filter(sg =>
        gameformat[key] ? sg[key] === gameformat[key] : false,
      )
      const r = {
        id: key,
        gameformats: rowGameformats,
      }
      return r
    })
    nearestGmfsByKey = nearestGmfsByKey.filter(i => i.gameformats.length)
    nearestGmfs = nearestGmfsByKey.reduce((a, b) => [...a, ...b.gameformats], [])
    const nearestIds = nearestGmfs.map(i => i.id)
    const nearestIsdWithHits = nearestIds.map(nId => ({
      id: nId,
      hits: nearestIds.filter(iId => iId === nId).length,
    }))
    const allhits = nearestIsdWithHits.map(i => i.hits)
    const maxHit = Math.max(...allhits)
    const nearestIdWithHit = nearestIsdWithHits.find(i => i.hits === maxHit)
    gmfWithMaxHits = nearestGmfs.find(i => i.id === nearestIdWithHit.id)
  }

  const type = gameformat?.type || 'general'
  const res =
    gameformats.find(i => i.is_primary) ||
    gmfWithMaxHits ||
    gameformats.find(i => i.type === type) ||
    gameformats.find(i => !i.preflop_only && i.subscription_tier) ||
    gameformats[0]
  return res
}

const getTableColumnKeys = ({ isPreflopOnly, isCashHu, isCash6max, isAnte4max, isCash9max }) => {
  const res = isPreflopOnly
    ? isAnte4max
      ? [
          'stack_rounding', //
          'type',
          'rake',
          'opening_size',
          'variant',
        ]
      : isCashHu
        ? [
            'stack_rounding', //
            'type',
            'rake',
            'opening_size',
            'variant',
          ]
        : isCash6max
          ? [
              'stack_rounding', //
              'type',
              'rake',
              'opening_size',
              'cash_drop',
              'variant',
            ]
          : [
              'stack_rounding', //
              'type',
              'rake',
              'opening_size',
            ]
    : isCashHu
      ? [
          'stack_rounding', //
          'type',
          'rake',
        ]
      : isCash9max
        ? [
            'stack_rounding', //
            'type',
            'rake',
            'opening_size',
            '_3bet_size',
          ]
        : [
            'stack_rounding', //
            'type',
            'rake',
            'opening_size',
            '_3bet_size',
            'simplified_position',
          ]
  // console.log(res)
  return res
}

export {
  getGameformat,
  getGameformatQuery,
  getGmfFilterQuery,
  getGmfTableQuery,
  getGameformatsFromGametypes,
  getGmfNameConf,
  getGameformatOptionHtml,
  getFilteredGameformats,
  getGameformatsByKey,
  getGmfGroupBlocks,
  getNestedGameformats,
  getActiveGmfSubs,
  getAllNestedGameformats,
  getGmfConfig,
  getStacksFromDepth,
  getPreferredGmf,
  getCustomTreeNameConf,
  getTableColumnKeys,
  getGameformatGroup,
}
